// Global Instructions Rule Applied!
//
// The "Part of a consolidated call" banner on the standard Capital Call
// form (owner ruling 2026-08-08: the separate plan modal is gone — the
// call form is the single surface for a consolidated call, and the gross
// SIZING lives here). Two modes:
//   - 'new'    — the form was opened via "New Consolidated Call"
//                (?fund_group=): the banner carries the plan fields the
//                modal used to hold — sizing (fixed gross amount OR % of
//                subscriptions), the drawdown line item type and its
//                description. First save creates the fund_group_calls
//                header + the materialised Draft primary call.
//   - 'linked' — an existing fund_group_call_id-linked call: same sizing
//                controls (editable while Draft), plus the Closed
//                subscription pool and the resolved gross amount from
//                fgc__call_detail.
//
// The %-of-subscriptions sizing resolves against the Closed SUBSCRIPTION
// pool (never capital_calls.total_percentage, whose pool is pocket
// commitments) — fgc__materialise_call pushes a resolved fixed
// total_amount onto the call. Purely presentational: the parent form owns
// the values and the save path (fgc__save_call stays the single canonical
// header writer).

import React from 'react'
import { Alert, Input, InputNumber, Segmented, Select, Space, Typography } from 'antd'

const { Text } = Typography

export interface ConsolidatedSizingValue {
  sizingMode: 'amount' | 'percentage'
  totalAmount: number | null
  /** edited as a percentage (0–100); stored server-side as a decimal fraction */
  totalPercentage: number | null
  itemTypeId: string | null
  description: string
}

interface ConsolidatedCallBannerProps {
  mode: 'new' | 'linked'
  groupName?: string | null
  /** Draft only — Issued consolidated calls render read-only */
  editable: boolean
  value: ConsolidatedSizingValue
  onChange: (patch: Partial<ConsolidatedSizingValue>) => void
  itemTypeOptions: Array<{ value: string; label: string }>
  /** the Closed subscription pool (the %-sizing base) */
  pool?: number | null
  poolFmt?: string | null
  /** server-resolved gross (linked mode) */
  grossFmt?: string | null
  currencySymbol?: string | null
}

const fmt = (value: number | null | undefined, symbol?: string | null) =>
  value == null ? '—' : `${symbol ?? ''}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`

export default function ConsolidatedCallBanner({
  mode,
  groupName,
  editable,
  value,
  onChange,
  itemTypeOptions,
  pool,
  poolFmt,
  grossFmt,
  currencySymbol
}: ConsolidatedCallBannerProps) {
  // client-side preview of the resolved gross while editing a % plan
  const resolvedPreview =
    value.sizingMode === 'percentage' && value.totalPercentage != null && pool != null
      ? fmt(Math.round(pool * (value.totalPercentage / 100) * 100) / 100, currencySymbol)
      : value.sizingMode === 'amount'
        ? fmt(value.totalAmount, currencySymbol)
        : '—'

  return (
    <Alert
      type='info'
      showIcon
      message={
        groupName ? `Part of a consolidated call — ${groupName}` : 'Part of a consolidated call'
      }
      description={
        <div className='flex flex-col gap-2'>
          <Text type='secondary'>
            {mode === 'new'
              ? 'Creating saves the consolidated plan and materialises this Draft call on the primary fund. Add fee and expense lines afterwards; the drawdown line absorbs gross total − fees − expenses; every non-fee line splits pro-rata by Consolidated Commitment.'
              : 'Line items are edited here while Draft. Compute calculates the call, every non-fee line shared by Consolidated Commitment; an amount you override here is kept, and clearing it puts the investor back on the calculated share (Recompute asks whether to keep or discard manual edits). Issue books the amounts shown on the primary fund ledger; Revert to Draft and Delete run through the same consolidated plan. The Consolidated Fund form (Capital Calls tab) offers the same actions plus Preview split.'}
          </Text>
          <Space wrap align='center'>
            <Text strong>Gross sizing:</Text>
            <Segmented
              size='small'
              disabled={!editable}
              value={value.sizingMode}
              options={[
                { label: 'Fixed gross amount', value: 'amount' },
                { label: '% of consolidated commitments', value: 'percentage' }
              ]}
              onChange={(v) => onChange({ sizingMode: v as 'amount' | 'percentage' })}
            />
            {value.sizingMode === 'percentage' ? (
              <InputNumber
                size='small'
                className='w-32'
                min={0.01}
                max={100}
                addonAfter='%'
                disabled={!editable}
                value={value.totalPercentage}
                onChange={(v) => onChange({ totalPercentage: v ?? null })}
              />
            ) : (
              <InputNumber
                size='small'
                className='w-40'
                min={0.01}
                disabled={!editable}
                value={value.totalAmount}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(v) => (v ? Number(v.replace(/,/g, '')) : 0) as 0}
                onChange={(v) => onChange({ totalAmount: (v as number) ?? null })}
              />
            )}
            {pool != null ? (
              <Text type='secondary'>
                Closed consolidated commitments: {poolFmt ?? fmt(pool, currencySymbol)}
              </Text>
            ) : null}
            <Text type='secondary'>
              Resolved gross: {grossFmt && value.sizingMode === 'percentage' ? grossFmt : resolvedPreview}
            </Text>
          </Space>
          <Space wrap align='center'>
            <Text strong>Drawdown (balance) line:</Text>
            <Select
              size='small'
              style={{ minWidth: 260 }}
              disabled={!editable}
              value={value.itemTypeId}
              options={itemTypeOptions}
              placeholder='Accounting item'
              showSearch
              optionFilterProp='label'
              onChange={(v) => onChange({ itemTypeId: v ?? null })}
            />
            <Input
              size='small'
              style={{ minWidth: 240 }}
              disabled={!editable}
              value={value.description}
              placeholder='Description (defaults to the call name)'
              onChange={(e) => onChange({ description: e.target.value })}
            />
          </Space>
        </div>
      }
    />
  )
}
